import { useState, useEffect } from 'react';
import { CheckCircle, XCircle, RefreshCw, Eye, EyeOff, ArrowRightLeft, Clock, Save, Check } from '../../icons/wp-icons';
import { pdsIntegrationApi } from '../../api/pdsIntegrationApi';
import { useAuth } from '../../context/AuthContext';

const ENTITY_OPTIONS = [
  { key: 'syncKunden', label: 'Kunden', richtungKey: 'kundenRichtung' },
  { key: 'syncAnfragen', label: 'Anfragen/Aufträge' },
  { key: 'syncTermine', label: 'Termine', richtungKey: 'terminRichtung' },
  { key: 'syncProjekte', label: 'Projekte' },
  { key: 'syncRechnungen', label: 'Rechnungen', richtungKey: 'rechnungRichtung' },
  { key: 'syncKva', label: 'Kostenvoranschläge' },
  { key: 'syncZeiterfassung', label: 'Zeiterfassung' },
  { key: 'syncKatalog', label: 'Katalog/Artikel' },
  { key: 'syncAnlagen', label: 'Anlagen & Wartung' },
];

const buildRichtungOptions = (appName) => [
  { value: 'EXPORT', label: `${appName} → PDS` },
  { value: 'IMPORT', label: `PDS → ${appName}` },
  { value: 'BIDIREKTIONAL', label: 'Bidirektional' },
];

function PdsIntegrationSettings({ readOnly = false }) {
  const { user } = useAuth();
  const appName = user?.organisation?.orgType === 'HEYKIKI' ? 'HeyKiki' : 'WerkPilot';
  const RICHTUNG_OPTIONS = buildRichtungOptions(appName);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);
  const [logs, setLogs] = useState([]);

  const [form, setForm] = useState({
    apiUrl: '',
    apiKey: '',
    apiUserName: '',
    aktiv: false,
    syncIntervallMinuten: 30,
    syncKunden: true,
    syncAnfragen: true,
    syncTermine: true,
    syncProjekte: true,
    syncRechnungen: true,
    syncKva: false,
    syncZeiterfassung: false,
    syncKatalog: false,
    syncAnlagen: false,
    kundenRichtung: 'BIDIREKTIONAL',
    terminRichtung: 'BIDIREKTIONAL',
    rechnungRichtung: 'EXPORT',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [integrationRes, statusRes] = await Promise.all([
        pdsIntegrationApi.get(),
        pdsIntegrationApi.getStatus().catch(() => null),
      ]);

      if (integrationRes.integration) {
        setForm(prev => ({
          ...prev,
          ...integrationRes.integration,
          apiKey: '', // Wird nie vom Server zurueckgegeben
        }));
      }

      if (statusRes) {
        setSyncStatus(statusRes);
      }

      // Letzte Logs laden
      const logsRes = await pdsIntegrationApi.getLogs({ limit: 10 }).catch(() => ({ logs: [] }));
      setLogs(logsRes.logs || []);
    } catch (error) {
      console.error('PDS Integration laden fehlgeschlagen:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (readOnly) return;
    setSaving(true);
    try {
      await pdsIntegrationApi.save(form);
      await loadData();
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error('PDS Integration speichern fehlgeschlagen:', error);
      alert('Fehler beim Speichern: ' + (error.response?.data?.error || error.message));
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const result = await pdsIntegrationApi.testConnection();
      setTestResult(result);
    } catch (error) {
      setTestResult({ success: false, error: error.message });
    } finally {
      setTesting(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      await pdsIntegrationApi.triggerSync();
      setTimeout(loadData, 3000); // Nach 3s Status neu laden
    } catch (error) {
      alert('Sync-Fehler: ' + (error.response?.data?.error || error.message));
    } finally {
      setSyncing(false);
    }
  };

  const updateForm = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  if (loading) {
    return (
      <div className="bg-surface rounded-card shadow-card p-6">
        <div className="flex items-center gap-3 mb-4">
          <img src="/logos/pds.svg" alt="PDS" className="h-6 w-auto" />
          <h2 className="text-lg font-semibold">PDS Integration</h2>
        </div>
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface rounded-card shadow-card p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <img src="/logos/pds.svg" alt="PDS Software" className="h-7 w-auto" />
          <div>
            <h2 className="text-lg font-semibold">PDS Software Integration</h2>
            <p className="text-sm text-gray-500">
              Synchronisiere Daten zwischen {appName} und PDS Handwerkersoftware
            </p>
          </div>
        </div>
        {syncStatus?.aktiv && (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
            <CheckCircle className="w-3.5 h-3.5" />
            Aktiv
          </span>
        )}
      </div>

      {/* API-Konfiguration */}
      <div className="space-y-4 mb-6">
        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">API-Konfiguration</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">PDS API-URL</label>
            <input
              type="url"
              value={form.apiUrl}
              onChange={(e) => updateForm('apiUrl', e.target.value)}
              placeholder="https://ihre-firma.pds.de/api"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary"
              disabled={readOnly}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">API-Benutzer (optional)</label>
            <input
              type="text"
              value={form.apiUserName}
              onChange={(e) => updateForm('apiUserName', e.target.value)}
              placeholder="api-user@firma.de"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary"
              disabled={readOnly}
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">API-Key</label>
            <div className="relative">
              <input
                type={showApiKey ? 'text' : 'password'}
                value={form.apiKey}
                onChange={(e) => updateForm('apiKey', e.target.value)}
                placeholder="Neuen API-Key eingeben (leer lassen um bestehenden zu behalten)"
                className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary"
                disabled={readOnly}
              />
              <button
                type="button"
                onClick={() => setShowApiKey(!showApiKey)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>

        {/* Verbindungstest */}
        <div className="flex items-center gap-3">
          <button
            onClick={handleTest}
            disabled={testing || !form.apiUrl}
            className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 flex items-center gap-2"
          >
            {testing ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <ArrowRightLeft className="w-4 h-4" />
            )}
            Verbindung testen
          </button>

          {testResult && (
            <span className={`flex items-center gap-1.5 text-sm ${testResult.success ? 'text-green-600' : 'text-red-600'}`}>
              {testResult.success ? (
                <>
                  <CheckCircle className="w-4 h-4" />
                  Verbindung erfolgreich ({testResult.dauerMs}ms)
                </>
              ) : (
                <>
                  <XCircle className="w-4 h-4" />
                  {testResult.error}
                </>
              )}
            </span>
          )}
        </div>
      </div>

      {/* Sync-Konfiguration */}
      <div className="space-y-4 mb-6">
        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Synchronisation</h3>

        <div className="flex items-center gap-3 mb-3">
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={form.aktiv}
              onChange={(e) => updateForm('aktiv', e.target.checked)}
              className="sr-only peer"
              disabled={readOnly}
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:ring-2 peer-focus:ring-primary/20 rounded-full peer peer-checked:bg-primary transition-colors"></div>
            <div className="absolute left-[2px] top-[2px] bg-white w-5 h-5 rounded-full transition-transform peer-checked:translate-x-5"></div>
          </label>
          <span className="text-sm font-medium">Automatische Synchronisation aktivieren</span>
        </div>

        <div className="w-48">
          <label className="block text-sm font-medium text-gray-700 mb-1">Sync-Intervall</label>
          <select
            value={form.syncIntervallMinuten}
            onChange={(e) => updateForm('syncIntervallMinuten', parseInt(e.target.value))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
            disabled={readOnly}
          >
            <option value={15}>Alle 15 Minuten</option>
            <option value={30}>Alle 30 Minuten</option>
            <option value={60}>Stündlich</option>
            <option value={360}>Alle 6 Stunden</option>
            <option value={1440}>Täglich</option>
          </select>
        </div>

        {/* Entity Toggles */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Welche Daten synchronisieren?</h4>
          <div className="space-y-3">
            {ENTITY_OPTIONS.map((entity) => (
              <div key={entity.key} className="flex items-center justify-between">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form[entity.key]}
                    onChange={(e) => updateForm(entity.key, e.target.checked)}
                    className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                    disabled={readOnly}
                  />
                  <span className="text-sm">{entity.label}</span>
                </label>

                {entity.richtungKey && form[entity.key] && (
                  <select
                    value={form[entity.richtungKey]}
                    onChange={(e) => updateForm(entity.richtungKey, e.target.value)}
                    className="text-xs px-2 py-1 border border-gray-200 rounded"
                    disabled={readOnly}
                  >
                    {RICHTUNG_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Sync Status */}
      {syncStatus?.configured && (
        <div className="space-y-3 mb-6">
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Status</h3>

          <div className="flex items-center gap-4 text-sm text-gray-600">
            {syncStatus.letzterSync && (
              <span className="flex items-center gap-1.5">
                <Clock className="w-4 h-4" />
                Letzter Sync: {new Date(syncStatus.letzterSync).toLocaleString('de-DE')}
              </span>
            )}
            <button
              onClick={handleSync}
              disabled={syncing || readOnly}
              className="px-3 py-1.5 text-xs bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 flex items-center gap-1.5"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${syncing ? 'animate-spin' : ''}`} />
              Jetzt synchronisieren
            </button>
          </div>

          {/* Letzte Sync-Fehler */}
          {syncStatus.recentErrors?.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <h4 className="text-xs font-medium text-red-800 mb-2">Letzte Fehler:</h4>
              <ul className="space-y-1">
                {syncStatus.recentErrors.slice(0, 3).map((err) => (
                  <li key={err.id} className="text-xs text-red-700">
                    {err.entityTyp}: {err.fehlerNachricht}
                    <span className="text-red-400 ml-2">
                      {new Date(err.createdAt).toLocaleString('de-DE')}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Letzte Sync-Logs */}
      {logs.length > 0 && (
        <div className="space-y-3 mb-6">
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Sync-Protokoll</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <th className="pb-2 pr-3">Zeitpunkt</th>
                  <th className="pb-2 pr-3">Typ</th>
                  <th className="pb-2 pr-3">Richtung</th>
                  <th className="pb-2 pr-3">Status</th>
                  <th className="pb-2">Dauer</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {logs.map((log) => (
                  <tr key={log.id}>
                    <td className="py-1.5 pr-3 text-gray-600">
                      {new Date(log.createdAt).toLocaleString('de-DE')}
                    </td>
                    <td className="py-1.5 pr-3">{log.entityTyp}</td>
                    <td className="py-1.5 pr-3">
                      {log.richtung === 'WP_ZU_PDS' ? '→ PDS' : log.richtung === 'PDS_ZU_WP' ? '← PDS' : '↔'}
                    </td>
                    <td className="py-1.5 pr-3">
                      <span className={`inline-flex px-1.5 py-0.5 rounded text-xs font-medium ${
                        log.status === 'SYNCHRONISIERT' ? 'bg-green-100 text-green-700' :
                        log.status === 'FEHLER' ? 'bg-red-100 text-red-700' :
                        'bg-yellow-100 text-yellow-700'
                      }`}>
                        {log.status}
                      </span>
                    </td>
                    <td className="py-1.5 text-gray-500">{log.dauerMs ? `${log.dauerMs}ms` : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Speichern */}
      {!readOnly && (
        <div className="pt-4 border-t border-border">
          {saved && (
            <div className="flex items-center gap-2 p-3 mb-4 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm">
              <Check className="w-4 h-4" />
              PDS-Integration erfolgreich gespeichert
            </div>
          )}
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-primary text-white rounded-button font-medium hover:bg-primary-dark transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Speichern...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Einstellungen speichern
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
}

export default PdsIntegrationSettings;
